// A pasted position or game → a `GameState` to play on from. FEN gives a
// position with no history; PGN gives the position after its last move,
// history included. Pure: the import screen words the errors.
import { GameState } from './GameState.js';
import { parsePgn } from './pgn.js';
import { FIDE, type RuleSet } from './rules/index.js';

export type ImportFormat = 'fen' | 'pgn';

/** Why an import was refused: nothing pasted, unreadable text, or a game with nothing left to play. */
export type ImportError = 'empty' | 'fen' | 'pgn' | 'over';

export type ImportResult =
  | { readonly ok: true; readonly format: ImportFormat; readonly state: GameState }
  | { readonly ok: false; readonly error: ImportError };

const FEN_RANKS = 8;

/** A FEN is one line whose first field has eight `/`-separated ranks; anything else is read as PGN. */
function looksLikeFen(text: string): boolean {
  if (text.includes('\n') || text.startsWith('[')) return false;
  return (text.split(/\s+/)[0] ?? '').split('/').length === FEN_RANKS;
}

export function importGame(input: string, rules: RuleSet = FIDE): ImportResult {
  const text = input.replace(/\r\n?/g, '\n').trim();
  if (text === '') return { ok: false, error: 'empty' };

  let format: ImportFormat;
  let state: GameState;
  if (looksLikeFen(text)) {
    const parsed = GameState.tryFromFen(text, rules);
    if (typeof parsed === 'string') return { ok: false, error: 'fen' };
    format = 'fen';
    state = parsed;
  } else {
    const parsed = parsePgn(text, rules);
    if (!parsed.ok) return { ok: false, error: 'pgn' };
    format = 'pgn';
    state = parsed.game.state;
  }
  // Mate, stalemate or a dead position on the board: there is no move to play.
  return state.isOver ? { ok: false, error: 'over' } : { ok: true, format, state };
}
